import { yupResolver } from "@hookform/resolvers/yup";
import { DatePicker } from "antd";
import FormInput from "components/Common/Form/FormInput";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { useForwardOrderMultiple } from "hooks";
import { useListCollectorOfHub } from "hooks/order-collector/useOrderCollector";
import { useTimeslotCollectorList } from "hooks/timeslot-collector/useTimeslotCollector";
import moment from "moment";
import useLocationData from "pages/BloodCollectionOrder/BloodCollectionOrderForm/useLocationData";
import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import * as yup from "yup";
import { getPhoneValidation } from "constants/validate";

const CoordinateCollectorOrder = ({
  orderIds,
  isOpenCoordinateModal,
  setIsOpenCoordinateModal,
  hubId,
  orderDetail,
  refetchSuccess,
  clearSelect,
  isAddressLevel2: isAddressLevel2Prop,
}) => {
  const { t } = useTranslation(["bloodOrder"]);
  const isMultipleOrders = useMemo(
    () => orderIds && orderIds.length > 1,
    [orderIds]
  );
  const isAddressLevel2 = useMemo(
    () =>
      isAddressLevel2Prop !== undefined
        ? isAddressLevel2Prop
        : orderDetail?.is_new_address === "N",
    [isAddressLevel2Prop, orderDetail?.is_new_address]
  );

  const normalizedOrderDetail = useMemo(() => {
    const province_code =
      orderDetail?.province_code ||
      orderDetail?.customer_province_code ||
      orderDetail?.sender_province_code ||
      null;

    const rawWard =
      orderDetail?.ward_code ||
      orderDetail?.customer_ward_code ||
      orderDetail?.sender_ward_code ||
      null;

    const rawDistrict =
      orderDetail?.district_code ||
      orderDetail?.customer_district_code ||
      orderDetail?.sender_district_code ||
      null;

    const district_code = isAddressLevel2 ? null : rawDistrict;
    const ward_code = isAddressLevel2
      ? rawDistrict || orderDetail?.customer_district_code || null
      : rawWard;

    return { ...orderDetail, province_code, district_code, ward_code };
  }, [orderDetail, isAddressLevel2]);

  const schema = useMemo(
    () =>
      yup.object().shape({
        action_date: yup
          .string()
          .required(
            t("message.select_required", {
              target: t("label.action_date"),
            })
          )
          .nullable(),

        timeslot: yup
          .object()
          .required(
            t("message.select_required", {
              target: t("label.timeslot"),
            })
          )
          .nullable(),
        phone: yup
          .string()
          .nullable()
          .matches(
            getPhoneValidation(t).regex,
            getPhoneValidation(t).errorMessage
          )
          .when(getPhoneValidation(t).when),
        collector: yup
          .object()
          .required(
            t("message.select_required", {
              target: t("label.collector"),
            })
          )
          .nullable(),

        province_code: isMultipleOrders
          ? yup.object().nullable()
          : yup
              .object()
              .required(
                t("message.select_required", {
                  target: t("placeholder.province"),
                })
              )
              .nullable(),

        district_code: isMultipleOrders
          ? yup.object().nullable()
          : !isAddressLevel2
          ? yup
              .object()
              .required(
                t("message.select_required", {
                  target: t("placeholder.district"),
                })
              )
              .nullable()
          : yup.object().nullable(),

        ward_code: isMultipleOrders
          ? yup.object().nullable()
          : yup
              .object()
              .required(
                t("message.select_required", {
                  target: t("placeholder.ward"),
                })
              )
              .nullable(),
      }),
    [isMultipleOrders, isAddressLevel2, t]
  );

  const mainForm = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
  });

  const {
    reset,
    setValue,
    formState: { errors },
    control,
    getValues,
    register,
    trigger,
  } = mainForm;

  useEffect(() => {
    // if (!isOpenCoordinateModal || !orderDetail) return;
    // reset();

    // setValue("customer_name", orderDetail?.sender_contact_name || "");
    // setValue("phone", orderDetail?.sender_contact_phone || "");

    if (!isOpenCoordinateModal) return;

    if (isMultipleOrders || !orderDetail) {
      reset(
        {
          customer_name: "",
          phone: "",
          detail_address: "",
          note: "",
          action_date: null,
          timeslot: null,
          collector: null,
          province_code: null,
          district_code: null,
          ward_code: null,
        },
        {
          keepErrors: false,
          keepDirty: false,
          keepTouched: false,
        }
      );
    } else {
      const detailAddress =
        normalizedOrderDetail?.full_address ??
        orderDetail?.sender_address ??
        orderDetail?.reciever_address ??
        "";
      // setValue("detail_address", String(detailAddress));
      // setValue("note", "");

      // if (orderDetail?.action_date) {
      //   setValue("action_date", moment(orderDetail.action_date));
      // }

      // if (normalizedOrderDetail?.province_code) {

      reset(
        {
          customer_name: orderDetail?.sender_contact_name || "",
          phone: orderDetail?.sender_contact_phone || "",
          detail_address: String(detailAddress),
          note: "",
          action_date: orderDetail?.action_date
            ? moment(orderDetail.action_date)
            : null,
          timeslot: null,
          collector: null,
          province_code: null,
          district_code: null,
          ward_code: null,
        },
        {
          keepErrors: false,
          keepDirty: false,
          keepTouched: false,
        }
      );
    }
  }, [
    isOpenCoordinateModal,
    orderDetail,
    isMultipleOrders,
    normalizedOrderDetail,
    reset,
    // setValue,
  ]);

  const { data: timeslotList, refetch: fetchTimeslotList } =
    useTimeslotCollectorList({
      params: {
        page: -1,
        columnFilter: {
          "filter[hub_id]": hubId,
          "filter[record_status]": "O",
        },
      },
      enabled: false,
      key: "timeslot-coordinate",
    });

  useEffect(() => {
    // timeSlot.id (AllocateCollector) và timeslot_id (ManageOrders)
    const timeslotId = orderDetail?.timeSlot?.id || orderDetail?.timeslot_id;

    if (timeslotList?.items && timeslotId && isOpenCoordinateModal) {
      const timeslotOption = timeslotList.items.find(
        item => item.id === timeslotId
      );
      if (timeslotOption) {
        setValue("timeslot", {
          label: timeslotOption.name,
          value: timeslotOption.id,
        });
      }
    }
  }, [
    timeslotList,
    orderDetail?.timeSlot?.id,
    orderDetail?.timeslot_id,
    isOpenCoordinateModal,
    setValue,
  ]);

  const { data: listCollectorHub, refetch: fetchListCollectorHub } =
    useListCollectorOfHub({
      params: {
        hubIds: hubId,
      },
    });

  useEffect(() => {
    if (
      listCollectorHub &&
      orderDetail?.orderDelivery?.runner_id &&
      isOpenCoordinateModal
    ) {
      const collectorOption = listCollectorHub.find(
        item => item.id === orderDetail.orderDelivery.runner_id
      );
      if (collectorOption) {
        setValue("collector", {
          label: `${collectorOption.display_name} - ${collectorOption.email}`,
          value: collectorOption.id,
        });
      }
    }
  }, [
    listCollectorHub,
    orderDetail?.orderDelivery?.runner_id,
    isOpenCoordinateModal,
    setValue,
  ]);

  const onSuccessForwardOrder = () => {
    refetchSuccess && refetchSuccess();
    setIsOpenCoordinateModal(false);
    clearSelect && clearSelect();
  };
  const { mutate: onForwardOrder } = useForwardOrderMultiple(
    onSuccessForwardOrder
  );

  const {
    provinceOptions,
    onChangeProvince,
    districtOptions,
    onChangeDistrict,
    isDisableDistrictSelect,
    wardOptions,
    onChangeWard,
    isDisableWardSelect,
  } = useLocationData({
    mainForm,
    isAddressLevel2,
    orderDetail: normalizedOrderDetail,
    reloadKey: isOpenCoordinateModal,
  });

  const closeCoordinateModal = () => {
    setIsOpenCoordinateModal(false);
    reset();
    clearSelect && clearSelect();
  };

  useEffect(() => {
    if (orderIds && isOpenCoordinateModal) {
      fetchTimeslotList();
      fetchListCollectorHub();
    }
  }, [orderIds, isOpenCoordinateModal]);

  const onCoordinateSubmit = () => {
    trigger().then(isValid => {
      if (isValid) {
        const formValue = getValues();

        const customer_district_code = isAddressLevel2
          ? formValue.ward_code?.value
          : formValue.district_code?.value;

        const customer_ward_code = isAddressLevel2
          ? null
          : formValue.ward_code?.value;

        const bodyData = {
          transport_id: null,
          bus_id: null,
          ids: orderIds,
          runner_id: formValue.collector.value,
          shipping_type: 4,
          note: formValue?.note,
          action_date: moment(formValue?.action_date).format("YYYY-MM-DD"),
          timeslot_id: formValue?.timeslot?.value,
          customer_contact_name: formValue.customer_name,
          customer_contact_phone: formValue.phone,
          customer_address: formValue.detail_address,
          customer_province_code: formValue.province_code?.value,
          customer_district_code: customer_district_code,
          customer_ward_code: customer_ward_code,
        };
        onForwardOrder(bodyData);
      }
    });
  };
  // Province -> District -> Ward mapping (tuần tự)
  useEffect(() => {
    if (!isOpenCoordinateModal) return;
    const pv = normalizedOrderDetail?.province_code;
    if (pv && provinceOptions?.length) {
      const opt = provinceOptions.find(o => String(o.value) === String(pv));
      if (opt) {
        onChangeProvince(opt);
        setValue("province_code", opt, { shouldValidate: true });
      }
    }
  }, [
    isOpenCoordinateModal,
    provinceOptions,
    normalizedOrderDetail?.province_code,
  ]);

  useEffect(() => {
    if (!isOpenCoordinateModal || isAddressLevel2) return;
    const dt = normalizedOrderDetail?.district_code;
    if (dt && districtOptions?.length) {
      const opt = districtOptions.find(o => String(o.value) === String(dt));
      if (opt) {
        onChangeDistrict(opt);
        setValue("district_code", opt, { shouldValidate: true });
      }
    }
  }, [
    isOpenCoordinateModal,
    isAddressLevel2,
    districtOptions,
    normalizedOrderDetail?.district_code,
  ]);

  useEffect(() => {
    if (!isOpenCoordinateModal) return;
    const wdRaw = normalizedOrderDetail?.ward_code;
    const wardCodeToMap = isAddressLevel2
      ? wdRaw || normalizedOrderDetail?.customer_district_code
      : wdRaw;

    if (wardCodeToMap && wardOptions?.length) {
      let opt = wardOptions.find(
        o => String(o.value) === String(wardCodeToMap)
      );
      if (!opt && isAddressLevel2) {
        const target = String(wardCodeToMap).toLowerCase();
        opt = wardOptions.find(w => {
          const v = String(w.value).toLowerCase();
          const l = String(w.label).toLowerCase();
          return v === target || l.includes(target) || target.includes(l);
        });
      }
      if (opt) setValue("ward_code", opt, { shouldValidate: true });
    }
  }, [
    isOpenCoordinateModal,
    isAddressLevel2,
    wardOptions,
    normalizedOrderDetail?.ward_code,
    normalizedOrderDetail?.customer_district_code,
  ]);
  return (
    <>
      <Modal
        centered
        isOpen={isOpenCoordinateModal}
        toggle={closeCoordinateModal}
        scrollable
        size="xl"
        className="blood-order-coordinate-modal"
        style={{ zIndex: 1055 }}
      >
        <ModalHeader>Điều phối</ModalHeader>
        <ModalBody className="p-0">
          <div className="bg-white p-4" style={{ minHeight: "70vh" }}>
            <Form>
              <Row>
                <Col xs={12}>
                  <Label>
                    {t("label.action_date")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                </Col>
                <Col md={6} xs={12}>
                  <FormGroup>
                    <Controller
                      control={control}
                      name="action_date"
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          className="w-100"
                          placeholder={t("placeholder.select", {
                            target: t("label.date"),
                          })}
                          style={{
                            height: 38,
                          }}
                          disabledDate={current => {
                            if (!current) return true;
                            return current < moment().startOf("day");
                          }}
                        />
                      )}
                    />
                    {errors.action_date && (
                      <MessageFormValidate error={errors.action_date.message} />
                    )}
                  </FormGroup>
                </Col>
                <Col md={6} xs={12}>
                  <FormGroup>
                    <Controller
                      control={control}
                      name="timeslot"
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder={t("placeholder.select", {
                            target: t("label.timeslot"),
                          })}
                          options={
                            timeslotList?.items?.map(item => ({
                              label: item.name,
                              value: item.id,
                            })) || []
                          }
                          classNamePrefix="select"
                          isSearchable
                          className="basic-single w-100"
                        />
                      )}
                    />
                    {errors.timeslot && (
                      <MessageFormValidate error={errors.timeslot.message} />
                    )}
                  </FormGroup>
                </Col>
                <Col xs={12}>
                  <Label>
                    {t("label.collector")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <FormGroup>
                    <Controller
                      control={control}
                      name="collector"
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder={t("placeholder.select", {
                            target: t("label.collector"),
                          })}
                          options={
                            listCollectorHub?.map(item => ({
                              label: `${item.display_name} - ${item.email}`,
                              value: item.id,
                            })) || []
                          }
                          classNamePrefix="select"
                          isSearchable
                          className="basic-single w-100"
                        />
                      )}
                    />
                    {errors.collector && (
                      <MessageFormValidate error={errors.collector.message} />
                    )}
                  </FormGroup>
                </Col>
                <Col xs={12}>
                  <FormInput
                    mainForm={mainForm}
                    fieldName="note"
                    type="textarea"
                    t={t}
                    title={t("placeholder.note")}
                    rows={5}
                  />
                </Col>
                <Col xs={12}>
                  <Label>{t("label.delivery_address")} </Label>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <FormInput
                      isLabel={false}
                      t={t}
                      fieldName="detail_address"
                      placeholder={t("placeholder.detail_address")}
                      type="text"
                      mainForm={mainForm}
                      isFormControlStyle={false}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Controller
                      control={control}
                      name="province_code"
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder={t("placeholder.province")}
                          options={provinceOptions}
                          onChange={onChangeProvince}
                          isSearchable
                          className="basic-single w-100"
                          classNamePrefix="select"
                        />
                      )}
                    />
                    {errors.province_code && (
                      <MessageFormValidate
                        error={errors.province_code.message}
                      />
                    )}
                  </FormGroup>
                </Col>
                {!isAddressLevel2 && (
                  <Col xs={12} md={6}>
                    <FormGroup>
                      <Controller
                        control={control}
                        name="district_code"
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder={t("placeholder.district")}
                            options={districtOptions}
                            onChange={onChangeDistrict}
                            isSearchable
                            className="basic-single w-100"
                            classNamePrefix="select"
                            isDisabled={isDisableDistrictSelect}
                          />
                        )}
                      />
                      {errors.district_code && (
                        <MessageFormValidate
                          error={errors.district_code.message}
                        />
                      )}
                    </FormGroup>
                  </Col>
                )}
                <Col xs={12} md={6}>
                  <FormGroup>
                    <Controller
                      control={control}
                      name="ward_code"
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder={
                            isAddressLevel2
                              ? "Phường/Xã"
                              : t("placeholder.ward")
                          }
                          options={wardOptions}
                          onChange={onChangeWard}
                          isSearchable
                          className="basic-single w-100"
                          classNamePrefix="select"
                          isDisabled={isDisableWardSelect}
                        />
                      )}
                    />
                    {errors.ward_code && (
                      <MessageFormValidate error={errors.ward_code.message} />
                    )}
                  </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <FormInput
                      isLabel={false}
                      t={t}
                      fieldName="customer_name"
                      placeholder={t("placeholder.customer_name")}
                      type="text"
                      mainForm={mainForm}
                      isFormControlStyle={false}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} md={6}>
                  <FormGroup>
                    <FormInput
                      isLabel={false}
                      t={t}
                      fieldName="phone"
                      placeholder={t("placeholder.phone")}
                      type="text"
                      mainForm={mainForm}
                      isFormControlStyle={false}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12}>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button color="danger" onClick={closeCoordinateModal}>
                      Huỷ
                    </Button>
                    <Button color="primary" onClick={onCoordinateSubmit}>
                      Xác nhận
                    </Button>
                  </div>
                </Col>
              </Row>
            </Form>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default CoordinateCollectorOrder;
